import React, { useEffect, useRef, useState } from 'react';
import { FaCreditCard, FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { checkoutPhotoCart, checkoutPhotoCartPiSpi, getPiSpiPaymentStatus } from '../services/api';
import waveLogo from '../assets/wave-logo.jpg';
import cinetpayLogo from '../assets/cinetpay-logo.jpg';
import pispiLogo from '../assets/pispi-logo.png';

const PAYMENT_METHODS = {
  cinetpay: { label: 'CinetPay', logo: cinetpayLogo },
  pispi: { label: 'PI-SPI', logo: pispiLogo },
  wave_ci: { label: 'Wave', logo: waveLogo },
};

const PISPI_POLL_INTERVAL_MS = 3000;

const getPhotoDisplayName = (photo) => {
  const name = photo?.name || photo?.title || photo?.filename || `Photo ${photo?.id || ''}`;
  return String(name).split('/').pop() || name;
};

/**
 * Print everything the gateway and the provider said about a failed payment.
 * CinetPay answers HTTP 200 even when it rejects the payload, so the reason
 * lives in `details.errors` rather than in the status code.
 */
const logPaymentFailure = (label, payload) => {
  const details = payload?.cinetpay?.details || payload?.details;
  const fieldErrors = payload?.cinetpay_errors || details?.errors;

  console.group(`%c[paiement] ${label}`, 'color:#c0392b;font-weight:bold');
  console.log('message:', payload?.message || '(aucun)');
  console.log('code fournisseur:', details?.status || payload?.error || '(aucun)');
  if (fieldErrors) console.table(fieldErrors);
  console.log('réponse complète:', payload);
  console.groupEnd();
};

/** Turn a gateway/CinetPay failure into the clearest message we can show. */
const buildPaymentError = (payload) => {
  logPaymentFailure('Aucun lien de paiement renvoyé', payload);

  const details = payload?.cinetpay?.details || payload?.details;
  const fieldErrors = payload?.cinetpay_errors || details?.errors;

  if (fieldErrors && typeof fieldErrors === 'object') {
    const flat = Object.entries(fieldErrors)
      .map(([field, messages]) => `${field} : ${[].concat(messages).join(' ')}`)
      .join(' — ');

    return new Error(`CinetPay a refusé la demande — ${flat}`);
  }

  return new Error(
    details?.message
    || payload?.message
    || "CinetPay n'a pas renvoyé de lien de paiement."
  );
};

const CartPaymentModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const { items: cartItems, clearCart } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentData, setPaymentData] = useState({
    method: 'cinetpay',
    billingEmail: user?.email || '',
    mobileNumber: '',
    payeurAlias: '',
  });
  const [pispiRequest, setPispiRequest] = useState(null);
  const pispiPollRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pispiPollRef.current) clearInterval(pispiPollRef.current);
    };
  }, []);

  if (!isOpen) return null;

  const cartPhotoIds = cartItems.map(item => item.id);
  const cartTotal = cartItems.reduce((sum, photo) => sum + Number(photo.price || 0), 0);
  const activePaymentLabel = (PAYMENT_METHODS[paymentData.method] || PAYMENT_METHODS.cinetpay).label;
  const handlePaymentChange = (field, value) => {
    setPaymentData(prev => ({ ...prev, [field]: value }));
    setPaymentError('');
  };

  const validatePaymentForm = () => {
    if (
      paymentData.method === 'cinetpay'
      && (!paymentData.billingEmail.trim() || !paymentData.billingEmail.includes('@'))
    ) {
      return 'Saisissez un e-mail de facturation valide.';
    }

    if (paymentData.method === 'wave_ci') {
      const digits = paymentData.mobileNumber.replace(/\D/g, '');
      if (digits.length < 8 || digits.length > 14) return 'Saisissez un numéro Wave valide.';
    }

    if (paymentData.method === 'pispi' && paymentData.payeurAlias.trim().length < 3) {
      return 'Saisissez votre alias PI-SPI.';
    }

    return '';
  };

  const stopPispiPolling = () => {
    if (pispiPollRef.current) {
      clearInterval(pispiPollRef.current);
      pispiPollRef.current = null;
    }
  };

  const startPispiPolling = (txId, methodLabel, purchasedPhotoIds) => {
    pispiPollRef.current = setInterval(async () => {
      try {
        const response = await getPiSpiPaymentStatus(txId);
        const paymentStatus = response?.payment_status;

        if (paymentStatus === 'completed') {
          stopPispiPolling();
          clearCart();
          window.dispatchEvent(new CustomEvent('photomatch:cart-checkout-success', {
            detail: { photoIds: purchasedPhotoIds, methodLabel },
          }));
          onSuccess?.(purchasedPhotoIds, methodLabel, response);
          setPispiRequest(null);
          onClose();
        } else if (paymentStatus === 'failed') {
          stopPispiPolling();
          setPispiRequest(prev => (prev ? { ...prev, status: 'failed' } : prev));
        }
      } catch (err) {
        // Transient network/API errors during polling are ignored; polling continues until expiry.
      }
    }, PISPI_POLL_INTERVAL_MS);
  };

  const handleCheckoutCart = async (e) => {
    e.preventDefault();

    if (cartPhotoIds.length === 0) {
      setPaymentError('Votre panier est vide');
      return;
    }

    const validationError = validatePaymentForm();
    if (validationError) {
      setPaymentError(validationError);
      return;
    }

    setCheckingOut(true);
    setPaymentError('');
    try {
      const purchasedPhotoIds = [...cartPhotoIds];
      const methodLabel = PAYMENT_METHODS[paymentData.method]?.label || 'paiement';

      if (paymentData.method === 'pispi') {
        const response = await checkoutPhotoCartPiSpi(purchasedPhotoIds, paymentData.payeurAlias.trim());

        if (response?.already_completed) {
          clearCart();
          window.dispatchEvent(new CustomEvent('photomatch:cart-checkout-success', {
            detail: { photoIds: purchasedPhotoIds, methodLabel },
          }));
          onSuccess?.(purchasedPhotoIds, methodLabel, response);
          onClose();
          return;
        }

        if (!response?.tx_id) {
          throw new Error("La demande de paiement PI-SPI n'a pas été créée.");
        }

        window.dispatchEvent(new CustomEvent('photomatch:cart-checkout-started', {
          detail: { photoIds: purchasedPhotoIds, methodLabel },
        }));
        setPispiRequest({
          txId: response.tx_id,
          status: 'pending',
          dateLimitePaiement: response.date_limite_paiement,
          purchasedPhotoIds,
          methodLabel,
        });
        startPispiPolling(response.tx_id, methodLabel, purchasedPhotoIds);
        return;
      }

      const response = await checkoutPhotoCart(purchasedPhotoIds, {
        method: paymentData.method,
        mobile_number: paymentData.mobileNumber,
        billing_email: paymentData.billingEmail || user?.email || '',
      });

      if (response?.already_completed) {
        clearCart();
        window.dispatchEvent(new CustomEvent('photomatch:cart-checkout-success', {
          detail: { photoIds: purchasedPhotoIds, methodLabel },
        }));
        onSuccess?.(purchasedPhotoIds, methodLabel, response);
        onClose();
        return;
      }

      if (!response?.payment_url) {
        throw buildPaymentError(response);
      }

      window.dispatchEvent(new CustomEvent('photomatch:cart-checkout-started', {
        detail: { photoIds: purchasedPhotoIds, methodLabel },
      }));
      onSuccess?.(purchasedPhotoIds, methodLabel, response);
      clearCart();
      window.location.assign(response.payment_url);
    } catch (err) {
      // A rejected request carries the API's JSON body, not an Error instance.
      if (!(err instanceof Error)) {
        logPaymentFailure('Requête refusée', err);
      }

      const details = err?.cinetpay?.details;
      setPaymentError(
        details?.message || err?.message || 'Le paiement a échoué'
      );
    } finally {
      setCheckingOut(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={() => !checkingOut && onClose()}
    >
      <form
        onSubmit={handleCheckoutCart}
        style={{
          width: '100%',
          maxWidth: '520px',
          maxHeight: '90vh',
          overflowY: 'auto',
          background: '#FFFFFF',
          borderRadius: '14px',
          padding: '1.5rem',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h3 style={{ margin: 0, color: 'var(--primary-orange)' }}>Paiement</h3>
            <p style={{ margin: '0.25rem 0 0', color: 'var(--gray)', fontSize: '0.9rem' }}>
              {cartPhotoIds.length} photo{cartPhotoIds.length !== 1 ? 's' : ''} · {Number(cartTotal).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} F
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={checkingOut}
            style={{
              border: 'none',
              background: 'var(--bg)',
              color: 'var(--dark-gray)',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              cursor: checkingOut ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FaTimes />
          </button>
        </div>

        <div style={{
          padding: '0.85rem',
          background: 'var(--bg)',
          borderRadius: '10px',
          marginBottom: '1rem',
          display: 'grid',
          gap: '0.5rem',
        }}>
          {cartItems.map(photo => (
            <div key={photo.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', color: 'var(--dark-gray)', fontSize: '0.9rem' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {getPhotoDisplayName(photo)}
              </span>
              <strong>{Number(photo.price || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} F</strong>
            </div>
          ))}
        </div>

        {paymentError && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>{paymentError}</div>
        )}

        {paymentData.method === 'cinetpay' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <img
                src={PAYMENT_METHODS.cinetpay.logo}
                alt="CinetPay"
                style={{
                  width: '48px',
                  height: '48px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                }}
              />
              <strong style={{ color: 'var(--dark-gray)' }}>Paiement sécurisé par CinetPay</strong>
            </div>
            <div className="form-group">
              <label>E-mail de facturation</label>
              <input
                type="email"
                value={paymentData.billingEmail}
                onChange={e => handlePaymentChange('billingEmail', e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>
          </>
        )}

        {paymentData.method === 'pispi' && !pispiRequest && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <img
                src={PAYMENT_METHODS.pispi.logo}
                alt="PI-SPI"
                style={{
                  width: '48px',
                  height: '48px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                }}
              />
              <strong style={{ color: 'var(--dark-gray)' }}>Paiement instantané PI-SPI (BCEAO)</strong>
            </div>
            <div className="form-group">
              <label>Votre alias PI-SPI</label>
              <input
                type="text"
                value={paymentData.payeurAlias}
                onChange={e => handlePaymentChange('payeurAlias', e.target.value)}
                placeholder="Alias enregistré sur votre application PI-SPI"
                autoComplete="off"
              />
            </div>
            <p style={{ margin: '0 0 1rem', color: 'var(--gray)', fontSize: '0.85rem' }}>
              Une demande de paiement de {Number(cartTotal).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} F sera envoyée à votre application PI-SPI. Vous aurez environ 2 minutes pour la confirmer.
            </p>
          </>
        )}

        {pispiRequest && (
          <div style={{
            border: `1px solid ${pispiRequest.status === 'failed' ? 'rgba(239, 68, 68, 0.35)' : 'var(--border)'}`,
            borderRadius: '10px',
            padding: '1rem',
            marginBottom: '1rem',
            background: pispiRequest.status === 'failed' ? 'rgba(239, 68, 68, 0.12)' : 'var(--surface-2)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem',
          }}>
            {pispiRequest.status === 'failed' ? (
              <FaExclamationTriangle style={{ color: '#F87171', width: '22px', height: '22px', flexShrink: 0, marginTop: '2px' }} />
            ) : (
              <div className="spinner" style={{ width: '22px', height: '22px', flexShrink: 0 }} />
            )}
            <div>
              <strong style={{ color: 'var(--dark-gray)', display: 'block', marginBottom: '0.35rem' }}>
                {pispiRequest.status === 'failed' ? 'Paiement PI-SPI refusé ou expiré' : 'En attente de confirmation PI-SPI'}
              </strong>
              <p style={{ margin: 0, color: 'var(--gray)', fontSize: '0.85rem' }}>
                {pispiRequest.status === 'failed'
                  ? 'La demande a été rejetée, annulée ou a expiré. Vous pouvez réessayer.'
                  : `Ouvrez votre application PI-SPI pour confirmer le paiement de ${Number(cartTotal).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} F. Transaction ${pispiRequest.txId}.`}
              </p>
              {pispiRequest.status === 'failed' && (
                <button
                  type="button"
                  onClick={() => { stopPispiPolling(); setPispiRequest(null); }}
                  style={{
                    marginTop: '0.6rem',
                    border: 'none',
                    background: 'var(--primary-orange)',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '0.5rem 0.9rem',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '0.82rem',
                  }}
                >
                  Réessayer
                </button>
              )}
            </div>
          </div>
        )}

        {paymentData.method === 'wave_ci' && (
          <>
            <div className="form-group">
              <label>Numéro {activePaymentLabel}</label>
              <input
                type="tel"
                inputMode="tel"
                value={paymentData.mobileNumber}
                onChange={e => handlePaymentChange('mobileNumber', e.target.value.replace(/[^\d+ ]/g, '').slice(0, 18))}
                placeholder="+225 07 00 00 00 00"
                autoComplete="tel"
              />
            </div>

            <div style={{
              border: '1px solid var(--border)',
              borderRadius: '10px',
              padding: '1rem',
              marginBottom: '1rem',
              background: '#F7F8FC',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.35rem' }}>
                <img
                  src={waveLogo}
                  alt="Wave"
                  style={{
                    width: '44px',
                    height: '44px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                  }}
                />
                <strong style={{ color: 'var(--dark-gray)' }}>Paiement Wave avec CinetPay</strong>
              </div>
              <p style={{ margin: '0.35rem 0 0', color: 'var(--gray)', fontSize: '0.9rem' }}>
                Vous serez redirigé vers CinetPay pour finaliser le paiement de {Number(cartTotal).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} F.
              </p>
            </div>
          </>
        )}

        {!pispiRequest && (
          <button
            type="submit"
            disabled={checkingOut}
            className="btn-submit"
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '0.45rem',
            }}
          >
            <FaCreditCard />{' '}
            {checkingOut
              ? (paymentData.method === 'pispi' ? 'Envoi de la demande PI-SPI...' : 'Ouverture de CinetPay...')
              : 'Payer'}
          </button>
        )}
      </form>
    </div>
  );
};

export default CartPaymentModal;
